import { useSession } from "next-auth/react";

import { tierLabel } from "@/components/shared/tier-simulation";
import type { SubscriptionStatusRecord } from "@/services/subscriptions.service";
import { usageService, type UsageResponse } from "@/services/usage.service";

/**
 * What a member may open, from the backend entitlement (`/usage/summary` ← TIER_LIMITS max_profiles /
 * max_pods) and the session's is_admin. Replaces the legacy Whop product-name parse (checkPermLevel):
 * product_title is NULL on every live subscription, which read every paying member as scope "none".
 */
export type MemberScope = "admin" | "multi_book" | "pods";

type ScopeSession = { user?: { is_admin?: boolean } } | null | undefined;
type ScopeUsage = Pick<UsageResponse, "profiles"> & { pods?: UsageResponse["pods"] };

export function memberScopes(session: ScopeSession, usage: ScopeUsage | undefined): MemberScope[] {
  const scopes: MemberScope[] = [];
  if (session?.user?.is_admin) scopes.push("admin");
  const books = usage?.profiles?.limit ?? 0;
  if (books === -1 || books > 1) scopes.push("multi_book");
  const pods = usage?.pods?.limit ?? 0;
  if (pods === -1 || pods > 0) scopes.push("pods");
  return scopes;
}

export function useMemberScopes(): MemberScope[] {
  const { data: session } = useSession();
  const { data: usage } = usageService.useUsageData({ enabled: !!session?.accessToken });
  return memberScopes(session, usage);
}

/** Top-nav plan badge: the backend's resolved tier for an active subscription, "—" when unknown. */
export function planBadgeLabel(status: Pick<SubscriptionStatusRecord, "status" | "tier"> | undefined): string {
  return status?.status === "active" && status.tier ? tierLabel(status.tier) : "—";
}
